/**
 * FilterPillButton Component
 * 
 * Reusable pill-shaped button for filter option selection.
 * Supports selected/unselected states with proper styling.
 * 
 * Design Specifications:
 * - Height: 40px fixed
 * - Padding: 12px vertical, 16px horizontal
 * - Border radius: 16px
 * - Selected: Background #01A7C8, white text, no border
 * - Unselected: Background #DBF7F8, teal text #007CAD, 2px solid border #DBF7F8
 * - Gap: 10px (for flex layout with icons)
 */

import { HOME_V2_COLORS } from '../colors';

interface FilterPillButtonProps {
  /** Button label text */
  label: string;
  /** Whether this option is currently selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Optional disabled state */
  disabled?: boolean;
}

export default function FilterPillButton({
  label,
  isSelected,
  onClick,
  disabled = false,
}: FilterPillButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        flex
        items-center
        justify-center
        gap-[10px]
        h-[40px]
        px-4
        py-3
        min-w-[64px]
        font-black
        text-xs
        uppercase
        tracking-[0.72px]
        leading-none
        rounded-2xl
        transition-all
        duration-150
        ease-in-out
        focus:outline-none
        disabled:opacity-50
        disabled:cursor-not-allowed
      `}
      style={{
        backgroundColor: isSelected 
          ? HOME_V2_COLORS.pillSelectedBg 
          : HOME_V2_COLORS.pillUnselectedBg,
        color: isSelected 
          ? HOME_V2_COLORS.pillSelectedText 
          : HOME_V2_COLORS.pillUnselectedText,
        border: isSelected
          ? 'none'
          : `2px solid ${HOME_V2_COLORS.pillUnselectedBg}`,
        // Focus ring color
        '--tw-ring-color': HOME_V2_COLORS.pillSelectedBg,
      } as React.CSSProperties}
      aria-pressed={isSelected}
    >
      {label}
    </button>
  );
}
